<script setup>
import TvSection from '~/components/tv/TvSection.vue';
import TvScorebug from '~/components/tv/TvScorebug.vue';
import {TV_COMPETITION_ICON, tvAgo} from '~/utils/tvDisplay';

// Always on screen: the newest results as scorebugs, the competition and how
// long ago above each. Fresh ones (arrived while the screen was on) are marked.
const props = defineProps({
  results: {type: Array, default: () => []},
  freshIds: {type: Object, default: () => new Set()},
});

// As many whole results as the card has room for
const listEl = ref(null);
const {count, remeasure} = useFitCount(listEl, '.tv-col-item', 10);
const shown = computed(() => props.results.slice(0, count.value));
watch(() => props.results.map(r => r.fixtureId).join(), () => remeasure());
</script>

<template>
  <tv-section icon="mdi-scoreboard" title="Latest results">
    <div v-if="shown.length" ref="listEl" class="tv-col-box">
    <transition-group name="tv-in" tag="div" class="tv-col-list">
      <div v-for="result in shown" :key="result.fixtureId" class="tv-col-item">
        <div class="tv-col-meta">
          <span v-if="freshIds.has(result.fixtureId)" class="tv-col-new">New</span>
          <v-icon :icon="TV_COMPETITION_ICON[result.competitionKind]" size="22"/>
          <span class="tv-col-comp">{{ result.competitionName }}</span>
          <span class="tv-col-when">{{ result.provisional ? 'To be confirmed' : tvAgo(result.playedAt) }}</span>
        </div>
        <tv-scorebug :result="result" size="list"/>
      </div>
    </transition-group>
    </div>
    <div v-else class="tv-col-empty">
      <v-icon icon="mdi-scoreboard-outline" size="30"/>
      <p>Results appear here as soon as they're entered</p>
    </div>
  </tv-section>
</template>

<style scoped lang="scss">
.tv-col-box {
  height: 100%;
  overflow: hidden;
}

.tv-col-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tv-col-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 20px;
  color: var(--tv-muted);
}

.tv-col-comp {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-col-when {
  margin-left: auto;
  white-space: nowrap;
}

.tv-col-new {
  padding: 0 10px;
  border-radius: 4px;
  background: var(--tv-accent);
  color: var(--tv-on-accent);
  font-weight: 600;
}

.tv-col-empty {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 4px;
  font-size: 22px;
  color: var(--tv-muted);

  p {
    margin: 0;
    max-width: 380px;
  }
}

.tv-in-enter-active {
  transition: transform 0.6s cubic-bezier(.2, 1, .3, 1), opacity 0.4s;
}

.tv-in-enter-from {
  transform: translateX(60px);
  opacity: 0;
}

.tv-in-move {
  transition: transform 0.5s ease;
}

.tv-in-leave-active {
  display: none;
}
</style>
